/**
 * Polish PWZ checksum — kept in sync with
 * `functions/python/chariot/registration/pwz.py`.
 *
 * Format: KABCDEF where K is the check digit of A–F weighted 1–6 (mod 11).
 * mod 11 == 10 is invalid; numbers may not start with 0.
 */

/**
 * Doctor launch (docs/reference/doctor-launch-plan-2026-10.md §4): every
 * account gives a PWZ number, which the founders check by hand against the NIL
 * register. Journey is register (name, PWZ, specialty) → AI info / terms → app
 * home; accounts without a PWZ (invited and pilot doctors) are asked for it at
 * /onboarding on their next visit. Admins are exempt.
 * Keep in sync with `chariot.registration.PWZ_REQUIRED`.
 */
export const PWZ_REQUIRED = true;

const PWZ_RE = /^[1-9]\d{6}$/;
const WEIGHTS = [1, 2, 3, 4, 5, 6] as const;
// What signup accepts: digits only, a sane length. Kept in sync with
// `chariot.registration.pwz.is_acceptable_pwz`; the manual review is the real
// check, the checksum below is only a flag for it.
const ACCEPTABLE_RE = /^\d{1,12}$/;

export function normalizePwz(value: string | null | undefined): string {
  if (value == null) return '';
  return String(value).trim().replace(/\s+/g, '');
}

/** True when the value is digits only (after whitespace removal). This is the
 * signup gate; `isValidPwz` is only a hint for typos. */
export function isAcceptablePwz(value: string | null | undefined): boolean {
  return ACCEPTABLE_RE.test(normalizePwz(value));
}

export function isValidPwz(value: string | null | undefined): boolean {
  const pwz = normalizePwz(value);
  if (!PWZ_RE.test(pwz)) return false;
  let total = 0;
  for (let i = 0; i < 6; i += 1) {
    total += Number(pwz[i + 1]) * WEIGHTS[i];
  }
  const check = total % 11;
  if (check === 10) return false;
  return check === Number(pwz[0]);
}

/** Current terms document version — must match backend CURRENT_TERMS_VERSION. */
export const CURRENT_TERMS_VERSION = '2026-10';
